import { CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Tag } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { DATE_RANGE_LABEL_KEYS } from "../helpers/date-range-filter.ts"
import { EMPTY_FILES_FILTERS, type FilesFilters } from "../types/files-filters.types.ts"

export interface FilterLabelLookup {
  fileTypes: { label: string; value: string }[]
  folders: { label: string; value: string }[]
  albums: { label: string; value: string }[]
  documentTypes: { label: string; value: string }[]
  modelTypes: { label: string; value: string }[]
}

interface FilesActiveFiltersProps {
  filters: FilesFilters
  labels: FilterLabelLookup
  onChange: (filters: FilesFilters) => void
}

interface Chip {
  key: string
  label: string
  clear: Partial<FilesFilters>
}

export function FilesActiveFilters({
  filters,
  labels,
  onChange
}: FilesActiveFiltersProps) {
  const { t } = useTranslation()
  const labelOf = (options: { label: string; value: string }[], value: string) =>
    options.find((option) => option.value === value)?.label ?? value

  const chips: Chip[] = []

  if (filters.file_type) {
    chips.push({
      key: "file_type",
      label: labelOf(labels.fileTypes, filters.file_type),
      clear: { file_type: null }
    })
  }
  if (filters.document_type) {
    chips.push({
      key: "document_type",
      label: labelOf(labels.documentTypes, filters.document_type),
      clear: { document_type: null }
    })
  }
  if (filters.folder) {
    const folderLabel = labelOf(labels.folders, filters.folder)
    chips.push({
      key: "folder",
      label: filters.folder_include_subfolders
        ? t("files.filters.folderChipWithSubfolders", { name: folderLabel })
        : folderLabel,
      clear: {
        folder: null,
        folder_include_subfolders: EMPTY_FILES_FILTERS.folder_include_subfolders
      }
    })
  }
  if (filters.album) {
    chips.push({
      key: "album",
      label: labelOf(labels.albums, filters.album),
      clear: { album: null }
    })
  }
  if (filters.owner) {
    chips.push({ key: "owner", label: filters.owner, clear: { owner: null } })
  }
  if (filters.related_record_type && filters.related_record_id) {
    const typeLabel = t(`files.linkRecord.types.${filters.related_record_type}`)
    chips.push({
      key: "related_record",
      label: `${typeLabel}: ${filters.related_record_label ?? filters.related_record_id}`,
      clear: {
        related_record_type: null,
        related_record_id: null,
        related_record_label: null
      }
    })
  }
  if (filters.model_type) {
    chips.push({
      key: "model_type",
      label: labelOf(labels.modelTypes, filters.model_type),
      clear: { model_type: null }
    })
  }
  if (filters.date_range) {
    const label =
      filters.date_range === "custom"
        ? [filters.date_after, filters.date_before].filter(Boolean).join(" – ") ||
          t("files.filters.dateRangePresets.custom")
        : t(`files.filters.dateRangePresets.${DATE_RANGE_LABEL_KEYS[filters.date_range]}`)
    chips.push({
      key: "date_range",
      label,
      clear: { date_range: null, date_after: null, date_before: null }
    })
  }

  if (!chips.length) return null

  return (
    <Flex align="center" gap="small" wrap="wrap">
      {chips.map((chip) => (
        <Tag
          key={chip.key}
          closable
          onClose={() => onChange({ ...filters, ...chip.clear })}
        >
          {chip.label}
        </Tag>
      ))}
      <Button
        type="link"
        size="small"
        icon={<CloseOutlined />}
        onClick={() => onChange(EMPTY_FILES_FILTERS)}
      >
        {t("files.filters.clearAll")}
      </Button>
    </Flex>
  )
}
